import { useSelectedPanelIndex } from "@/shared/hooks";
import { NavigationPanel } from "@/shared/types";
import {
	Panels,
	Box,
	Flex,
	FlexChild,
	TextColors,
	Typography,
	BorderColors,
	BorderRadius,
	Button,
	ButtonSize,
	Dropdown,
	FontFamilies,
	ForegroundColors,
} from "@abel-technologies-as/components";
import { useParams } from "react-router-dom";
import { NestedPageLayoutWrapper } from "@/shared/style";
import { Outlet } from "react-router";
import { PanelWrapper } from "./style";
import { Export } from "@/shared/icons";
import { t } from "i18next";
import styled from "styled-components";
import { useState } from "react";
import { GlobalExportLoader } from "@/shared/components/global-export-loader/GlobalExportLoader";
import { useExportEmployees } from "../hooks";
import { useTranslation } from "react-i18next";

const ExportItem = styled.li`
	cursor: pointer;
	padding: 8px 12px;
	width: 100%;
	&:hover {
		background-color: #f9fafb;
	}
`;

const panels: NavigationPanel[] = [
	{
		title: "All users",
		route: "",
	},
	{
		title: "Open DHC",
		route: "open-dhc",
	},
];

const EmployeesLayout = () => {
	const { companyId } = useParams();
	const [openExportDropdown, setOpenExportDropdown] = useState<boolean>(false);
	const { exportFormat, handleExport, handleCancelExport } = useExportEmployees();
	const { i18n } = useTranslation();

	const { currentSelectedIndex, handlePanelClick } = useSelectedPanelIndex(panels, `/companies/${companyId}/employees`);
	const handleExportFormat = (format: "csv" | "xlsx") => {
		if (!companyId) return;
		setOpenExportDropdown(false);
		handleExport(companyId, i18n.language, format);
	};
	return (
		<>
			{exportFormat && <GlobalExportLoader format={exportFormat} onCancel={handleCancelExport} />}

			<Box customStyles={{ marginBottom: "40px" }} />
			<Flex alignItems="flex-start" justifyContent="space-between" margin="0 0 16px 0">
				<FlexChild flexBasis="1" flexGrow={1} flexShrink={1}>
					<Typography
						variant="h2"
						color={TextColors.Primary}
						fontWeight={600}
						fontSize="18px"
						lineHeight="28px"
						fontFamily="Inter-Semibold">
						Employees
					</Typography>
					<Typography
						variant="h3"
						color={TextColors.Tertiary}
						fontWeight={400}
						fontFamily="Inter-Regular"
						fontSize="14px"
						lineHeight="20px">
						View and manage all employees in this company.
					</Typography>
				</FlexChild>
				<FlexChild flexBasis="1" flexGrow={1} flexShrink={1}>
					<Flex gap="8px" alignItems="center" justifyContent="flex-end">
						<Dropdown
							setOpenDropDown={setOpenExportDropdown}
							openDropDown={openExportDropdown}
							position="bottom"
							dropdownContainerStyle={{ width: "auto" }}
							customDropDownContent={
								<ul style={{ padding: "4px 0", minWidth: "120px" }}>
									<ExportItem onClick={() => handleExportFormat("csv")}>
										<Typography
											lineHeight="20px"
											color={TextColors.Secondary}
											fontWeight={500}
											fontFamily={FontFamilies.InterMedium}>
											CSV
										</Typography>
									</ExportItem>
									<ExportItem onClick={() => handleExportFormat("xlsx")}>
										<Typography
											lineHeight="20px"
											color={TextColors.Secondary}
											fontWeight={500}
											fontFamily={FontFamilies.InterMedium}>
											XLSX
										</Typography>
									</ExportItem>
								</ul>
							}
							triggerComponent={
								<Button
									borderSize={"1px"}
									borderRadius={`${BorderRadius.MD}px`}
									iconBefore={<Export width="16px" height={"16px"} />}
									boxShadow={"0px 1px 2px 0px rgba(16, 24, 40, 0.05)"}
									size={ButtonSize.SMALL}
									borderColor={BorderColors.Primary}
									onClick={() => setOpenExportDropdown(true)}>
									<Typography
										margin="0 0 0 4px"
										color={ForegroundColors.Secondary}
										fontSize="14px"
										fontWeight={600}
										lineHeight="18px"
										fontFamily="Inter-SemiBold">
										{t("export")}
									</Typography>
								</Button>
							}
						/>
					</Flex>
				</FlexChild>
			</Flex>
			<PanelWrapper>
				<Panels size={"small"} onClick={handlePanelClick} currentSelectedIndex={currentSelectedIndex} panels={panels} />
			</PanelWrapper>

			<NestedPageLayoutWrapper height="10">
				<Outlet />
			</NestedPageLayoutWrapper>
		</>
	);
};

export default EmployeesLayout;
